// The page's call to POST /api/keyboard/suggest. Null for anything but a
// contract answer (a network problem, an aborted request, an off-contract
// body), so the host can tell "no answer" from an answer and never mistakes
// one for a dead end.
import {
  type SuggestRequest,
  type SuggestResponse,
  suggestResponseSchema,
} from "@bindery/dynamic-keyboard";
import { KEYBOARD_SUGGEST_PATH } from "../contracts/keyboard-suggest";

export interface RequestSuggestionInput {
  readonly request: SuggestRequest;
  readonly getToken: () => Promise<string | null>;
  /** The keyboard's: aborted as soon as the child types on. */
  readonly signal?: AbortSignal;
  readonly fetch?: typeof fetch;
}

export const requestSuggestion = async ({
  request,
  getToken,
  signal,
  fetch: doFetch = (input, init) => fetch(input, init),
}: RequestSuggestionInput): Promise<SuggestResponse | null> => {
  let token: string | null;
  try {
    token = await getToken();
  } catch {
    return null;
  }
  if (token === null) return null;
  let body: unknown;
  try {
    const response = await doFetch(KEYBOARD_SUGGEST_PATH, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify(request),
      ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok) return null;
    body = await response.json();
  } catch {
    return null;
  }
  const parsed = suggestResponseSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
};
